import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/atoms/Badge';
import { Button } from '@/components/atoms/Buttons';
import { Overline } from '@/components/atoms/Eyebrow';
import FormField from '@/components/atoms/FormField';
import TextField from '@/components/atoms/TextField';
import { Disclosure } from '@/components/molecules/Disclosure';
import { ModalBody, ModalComponent } from '@/components/molecules/ModalComponent';
import type {
    FeedingDashboardConfig,
    UpdateFeedingDashboardConfig,
} from '@/domain/feedingDashboard';

import { FeedingConfigFooter } from './FeedingConfigFooter';
import { FeedingConfigScopeFields } from './FeedingConfigScopeFields';
import { C, type FieldDef, SECTIONS, TARGET_FIELDS } from './feedingConfigFields';
import { useFeedingConfigForm } from './useFeedingConfigForm';

import styles from './FeedingConfigPanel.module.scss';

export interface FeedingConfigPen {
    cageId: number;
    cageName: string;
    /** Resolved config: site defaults with this pen's overrides applied. */
    config: FeedingDashboardConfig;
}

export interface FeedingConfigPanelProps {
    config: FeedingDashboardConfig;
    /** Pens that can carry their own overrides. Empty renders the site form only. */
    pens?: FeedingConfigPen[];
    /**
     * The pellet flag alone. Off hides the depth threshold and the pellet bands in both
     * scopes. It deliberately ignores the pens' hardware: the response reports that only
     * for the filtered pens and the selected period, so a pen could lose its own depth
     * threshold (IPS req 29) to a filter or a date range.
     */
    showPelletSettings: boolean;
    saving?: boolean;
    onSave: (update: UpdateFeedingDashboardConfig) => Promise<void>;
    /** Write one pen's overrides. Null clears a field back to the site value. */
    onSavePen?: (cageId: number, update: UpdateFeedingDashboardConfig) => Promise<void>;
    /** The site's data sources page, linked from the footer; null for no link. */
    dataSourcesHref?: string | null;
}

/** Site-level feeding targets, caution margins and band thresholds (per location). */
export function FeedingConfigPanel(props: FeedingConfigPanelProps) {
    const { config, pens = [], saving, dataSourcesHref = null } = props;
    const { t } = useTranslation();
    const {
        open,
        scope,
        setScope,
        canScopeToPen,
        activePen,
        fieldVisible,
        values,
        setValue,
        openSections,
        toggleSection,
        saveError,
        systemName,
        setSystemName,
        errors,
        orderErrors,
        isValid,
        openModal,
        closeModal,
        handleSave,
        handleResetPen,
        resetToDefaults,
    } = useFeedingConfigForm(props);

    const renderField = (field: FieldDef) => {
        if (!fieldVisible(field.key)) return null;
        const invalid = errors[field.key];
        const orderInvalid = orderErrors[field.key];
        // In pen scope, say which numbers actually differ from the site. Without it the
        // form looks identical whether a pen inherits everything or overrides half of it.
        const overridden = activePen != null && activePen.config[field.key] !== config[field.key];
        const error = invalid
            ? t(`${C}.invalidValue`)
            : orderInvalid
              ? t(`${C}.orderError`)
              : undefined;
        return (
            <FormField
                key={field.key}
                size="sm"
                htmlFor={field.key}
                className={styles.field}
                label={
                    <>
                        {t(field.labelKey)}
                        {overridden ? (
                            <Badge
                                tone="accent"
                                variant="solid"
                                className={styles.overridden}
                                title={t(`${C}.overriddenHint`)}>
                                {t(`${C}.overridden`)}
                            </Badge>
                        ) : null}
                    </>
                }
                error={error}>
                <TextField
                    id={field.key}
                    type="number"
                    step={field.step}
                    min="0"
                    value={values[field.key]}
                    onChange={(event) => setValue(field.key, event.target.value)}
                />
            </FormField>
        );
    };

    // Escape and a backdrop click close it like the header button, but not mid-save, which
    // would hide the result of the write.
    return (
        <div className={styles.panel}>
            <Button variant="secondary" size="small" type="button" onClick={openModal}>
                {t(`${C}.title`)}
            </Button>
            <ModalComponent
                isOpen={open}
                onClose={closeModal}
                dismissible={!saving}
                centered={false}
                scrollable
                title={t(`${C}.title`)}>
                <ModalBody className={styles.body}>
                    <p className={styles.intro}>{t(`${C}.intro`)}</p>

                    <FeedingConfigScopeFields
                        scope={scope}
                        setScope={setScope}
                        canScopeToPen={canScopeToPen}
                        pens={pens}
                        activePen={activePen}
                        systemName={systemName}
                        setSystemName={setSystemName}
                    />

                    <section className={styles.targets}>
                        <Overline as="h3" className={styles.sectionTitle}>
                            {t(`${C}.sections.targets`)}
                        </Overline>
                        <div className={styles.fieldGrid}>{TARGET_FIELDS.map(renderField)}</div>
                    </section>

                    {SECTIONS.map((section) => {
                        if (!section.fields.some((field) => fieldVisible(field.key))) {
                            return null;
                        }
                        return (
                            <Disclosure
                                key={section.id}
                                variant="inline"
                                spacing="none"
                                title={t(section.titleKey)}
                                open={openSections.has(section.id)}
                                onOpenChange={() => toggleSection(section.id)}
                                className={styles.section}
                                triggerClassName={styles.sectionToggle}
                                panelClassName={styles.sectionBody}>
                                <p className={styles.hint}>{t(section.hintKey)}</p>
                                <div className={styles.fieldGrid}>
                                    {section.fields.map(renderField)}
                                </div>
                            </Disclosure>
                        );
                    })}
                </ModalBody>

                <FeedingConfigFooter
                    penScope={activePen != null}
                    saving={saving}
                    saveError={saveError}
                    isValid={isValid}
                    dataSourcesHref={dataSourcesHref}
                    onResetPen={handleResetPen}
                    onResetToDefaults={resetToDefaults}
                    onCancel={closeModal}
                    onSave={handleSave}
                />
            </ModalComponent>
        </div>
    );
}
